import { appKey } from '@app/shared/brand'
import { useEffect, useState } from 'react'

/**
 * Which build this bundle is, and what to do when the server has moved on.
 *
 * A deploy replaces the served assets wholesale, so the hashed chunks a running
 * tab has not loaded yet STOP EXISTING. Every route here is lazy, which makes
 * that concrete rather than theoretical: a tab open across a deploy asks for a
 * filename that is gone the first time somebody opens a section they had not
 * visited, and the import rejects inside Suspense.
 *
 * Two answers, and which one is right depends on what the surface can lose.
 */

/** Baked in at build time. `unknown` outside a release build. */
declare const __BUILD_COMMIT__: string
export const BUILD_COMMIT = __BUILD_COMMIT__

const CHUNK_RELOAD_AT = appKey('chunk-reload')
const CHUNK_RELOAD_QUIET_MS = 10_000

/**
 * A chunk that will not load is a deploy that happened underneath us, so the
 * page is reloaded onto the build that has it.
 *
 * Reloading is the whole repair, and it is safe here in a way an unprompted
 * reload elsewhere is not: this fires on a navigation the person just asked for,
 * whose alternative is a blank region.
 *
 * Bounded, because the same event fires when a chunk is missing for any other
 * reason. One reload is a fix; a reload every time it fails is a loop that never
 * shows the error that would explain it.
 */
export function installChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_AT) ?? 0)
    if (Date.now() - last < CHUNK_RELOAD_QUIET_MS) return
    event.preventDefault()
    sessionStorage.setItem(CHUNK_RELOAD_AT, String(Date.now()))
    window.location.reload()
  })
}

/**
 * Whether the server is serving a different build than this tab is running.
 *
 * Checked ON FOCUS rather than on a timer. Returning to a tab is exactly when it
 * has been sitting long enough to be stale, and a timer would spend a phone's
 * battery asking a question nobody is waiting on — the public zmanim page is
 * read on a phone in shul, which is the worst place to poll.
 *
 * A build that cannot name itself is not compared: in dev every reload would
 * otherwise look like a deploy. A failed request is not evidence of anything
 * either — being offline is not being out of date.
 */
export function useStaleBuild(): boolean {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    if (BUILD_COMMIT === 'unknown' || stale) return
    let dropped = false

    const check = async () => {
      if (document.visibilityState !== 'visible') return
      try {
        const res = await fetch('/version.json', { cache: 'no-store' })
        if (!res.ok) return
        const { commit } = (await res.json()) as { commit?: unknown }
        if (dropped || typeof commit !== 'string') return
        if (commit !== 'unknown' && commit !== BUILD_COMMIT) setStale(true)
      } catch {
        // Offline, or the deployment is mid-roll. Neither says this tab is old.
      }
    }

    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    return () => {
      dropped = true
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
    }
  }, [stale])

  return stale
}
